import React from 'react';
import { mealsAPI } from '../api/client';

const COLOR_DOT = {
  dark_green:  '#4ade80',
  green:       '#86efac',
  light_green: '#a3e635',
  yellow:      '#fbbf24',
  orange:      '#fb923c',
  red:         '#f87171',
};

const MEAL_ICONS = {
  breakfast: '🌅',
  lunch:     '☀️',
  snacks:    '🍎',
  dinner:    '🌙',
};

export default function DayDetail({ date, meals, dailyLog, onClose, onMealDeleted }) {
  const handleDelete = async (mealId) => {
    try {
      const res = await mealsAPI.deleteMeal(mealId);
      onMealDeleted(mealId, res.data.dailyLog);
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  const caloriesPct = dailyLog
    ? Math.min((dailyLog.totalCalories / dailyLog.calorieGoal) * 100, 100)
    : 0;

  const macros = dailyLog
    ? [
        { label: 'Protein', value: dailyLog.totalProtein, unit: 'g', color: '#818cf8' },
        { label: 'Carbs',   value: dailyLog.totalCarbs,   unit: 'g', color: '#a3e635' },
        { label: 'Fat',     value: dailyLog.totalFat,     unit: 'g', color: '#fb923c' },
        { label: 'Fiber',   value: dailyLog.totalFiber,   unit: 'g', color: '#4ade80' },
      ]
    : [];

  return (
    <div className="day-detail-wrap">

      {/* ── Header ── */}
      <div className="day-detail-header">
        <div>
          <h3 className="day-detail-title">{formatDate(date)}</h3>
          {dailyLog && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: COLOR_DOT[dailyLog.colorRating] || '#64748b',
                display: 'inline-block',
              }} />
              <span style={{ fontSize: 13, color: 'rgba(241,245,249,0.5)', fontFamily: 'Inter, sans-serif' }}>
                Health score:{' '}
                <strong style={{ color: COLOR_DOT[dailyLog.colorRating] || '#f1f5f9' }}>
                  {dailyLog.healthScore}/100
                </strong>
              </span>
            </div>
          )}
        </div>
        <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
      </div>

      {/* ── Daily Summary ── */}
      {dailyLog && (
        <div style={{ marginBottom: 20 }}>

          {/* Calories Progress */}
          <div className="calories-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, flexWrap: 'wrap', gap: 4 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, color: 'rgba(241,245,249,0.4)',
                textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: 'Inter, sans-serif',
              }}>
                Calories
              </span>
              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>
                <span style={{ color: caloriesPct > 100 ? '#f87171' : '#a3e635' }}>
                  {dailyLog.totalCalories}
                </span>
                <span style={{ color: 'rgba(241,245,249,0.3)', fontWeight: 400 }}>
                  {' '}/ {dailyLog.calorieGoal} kcal
                </span>
              </span>
            </div>
            <div style={{
              height: 8, background: 'rgba(255,255,255,0.07)',
              borderRadius: 99, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${caloriesPct}%`,
                background: caloriesPct > 100
                  ? 'linear-gradient(90deg, #f87171, #fb923c)'
                  : 'linear-gradient(90deg, #4ade80, #a3e635)',
                borderRadius: 99,
                transition: 'width 0.8s cubic-bezier(0.4,0,0.2,1)',
              }} />
            </div>
          </div>

          {/* Macros Grid — 4 col desktop, 2×2 on mobile */}
          <div className="macros-grid">
            {macros.map(({ label, value, unit, color }) => (
              <div key={label} className="macro-card">
                <div className="macro-label">{label}</div>
                <div className="macro-value" style={{ color }}>
                  {value}
                  <span className="macro-unit">{unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Risk Factors ── */}
      {dailyLog?.riskFactors?.length > 0 && (
        <div style={{
          background: 'rgba(251,191,36,0.06)',
          border: '1px solid rgba(251,191,36,0.15)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 12,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: '#fbbf24', marginBottom: 8,
            fontFamily: 'Inter, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            ⚠️ Risk factors
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {dailyLog.riskFactors.map((r, i) => (
              <li key={i} style={{ fontSize: 13, color: 'rgba(251,191,36,0.8)', fontFamily: 'Inter, sans-serif' }}>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Recommendations ── */}
      {dailyLog?.recommendations?.length > 0 && (
        <div style={{
          background: 'rgba(129,140,248,0.06)',
          border: '1px solid rgba(129,140,248,0.15)',
          borderRadius: 12, padding: '14px 16px', marginBottom: 20,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: '#818cf8', marginBottom: 8,
            fontFamily: 'Inter, sans-serif', letterSpacing: '0.04em', textTransform: 'uppercase',
          }}>
            💡 Recommendations
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {dailyLog.recommendations.map((r, i) => (
              <li key={i} style={{ fontSize: 13, color: 'rgba(129,140,248,0.8)', fontFamily: 'Inter, sans-serif' }}>
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Meal List ── */}
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
        color: 'rgba(241,245,249,0.3)', textTransform: 'uppercase',
        marginBottom: 12, fontFamily: 'Inter, sans-serif',
      }}>
        Meals logged
      </div>

      {meals.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '32px 16px',
          color: 'rgba(241,245,249,0.2)',
          fontSize: 14, fontFamily: 'Inter, sans-serif',
        }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🍽️</div>
          No meals logged for this day yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {meals.map((meal) => (
            <div
              key={meal._id}
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14, padding: '16px',
              }}
            >
              {/* Meal Type Header */}
              <div style={{
                display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', marginBottom: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{MEAL_ICONS[meal.mealType] || '🍽️'}</span>
                  <span style={{
                    fontSize: 13, fontWeight: 700, textTransform: 'capitalize',
                    color: '#f1f5f9', fontFamily: 'Inter, sans-serif',
                  }}>
                    {meal.mealType}
                  </span>
                </div>
                <button
                  onClick={() => handleDelete(meal._id)}
                  style={{
                    padding: '5px 12px', borderRadius: 7,
                    background: 'rgba(248,113,113,0.08)',
                    border: '1px solid rgba(248,113,113,0.18)',
                    color: '#f87171', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                    transition: 'all 0.2s', minHeight: 32,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.18)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(248,113,113,0.08)'}
                >
                  Delete
                </button>
              </div>

              {/* Food Items */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {meal.foodItems.map((item, i) => (
                  <div key={i} className="meal-item-row">
                    {/* Left: name + quantity + AI */}
                    <div className="meal-item-info">
                      <span style={{
                        fontSize: 13, fontWeight: 600, color: '#f1f5f9',
                        fontFamily: 'Inter, sans-serif',
                      }}>
                        {item.name}
                      </span>
                      <span style={{
                        fontSize: 12, color: 'rgba(241,245,249,0.35)',
                        marginLeft: 6, fontFamily: 'Inter, sans-serif',
                      }}>
                        {item.quantity}
                      </span>
                      {item.aiAnalysis && (
                        <div style={{
                          fontSize: 11, color: 'rgba(241,245,249,0.3)',
                          fontStyle: 'italic', marginTop: 3,
                          fontFamily: 'Inter, sans-serif',
                        }}>
                          {item.aiAnalysis}
                        </div>
                      )}
                    </div>

                    {/* Right: calories + score */}
                    <div className="meal-item-stats" style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: '#a3e635',
                        fontFamily: 'Inter, sans-serif',
                      }}>
                        {item.calories} cal
                      </div>
                      <div style={{ fontSize: 11, color: 'rgba(241,245,249,0.3)', fontFamily: 'Inter, sans-serif' }}>
                        score {item.healthScore}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
